import { box, line } from './projection'

/* ==========================================================================
   The machine kit shared by every variant: a pad/screen printing machine as
   a flat-ink cel illustration. One key light from the upper left front: tops
   lightest, fronts mid, right sides darkest; lit edges carry a crisp line;
   contact shadows are the footprint offset (+12, -4) in the next darker tone.
   Tones are classes (f-* fills, l-* lines) resolved in scene.css.
   Moving parts are authored with their local origin at the pivot.
   ========================================================================== */

const MATS = {
  g: ['f-gT', 'f-gF', 'f-gS'],
  d: ['f-gF', 'f-gS', 'f-gR'],
  a: ['f-aT', 'f-aF', 'f-aS'],
  s: ['f-sF', 'f-sE', 'f-sE'],
  p: ['f-pF', 'f-pW', 'f-pS'],
}
const LIT_LINE = { a: 'l-spec', d: 'l-gT', p: 'l-crest' }

/** A projected box: top, front, right side (+ optional lit edges and shadow). */
export function Box({ geo, mat = 'a', lit = true, shadow }) {
  const [top, front, side] = MATS[mat]
  return (
    <>
      {shadow && <path className={shadow} d={geo.shadow} />}
      <path className={side} d={geo.side} />
      <path className={front} d={geo.front} />
      <path className={top} d={geo.top} />
      {lit && <path className={LIT_LINE[mat] ?? 'l-hi'} d={`${geo.litTop}${geo.litLeft}`} />}
    </>
  )
}

/* ---- Set: floor, cabinet, table ---------------------------------------- */

const TABLE = { x0: 120, x1: 1400, z0: 0, z1: 240, h0: -56, h1: 0 }
const TABLE_BOX = box(TABLE)
const TABLE_SCREWS = Array.from({ length: 8 }, (_, i) => 200 + i * 160)
  .map((x) => `M${x + 3.2} 718a3.2 3.2 0 1 0 -6.4 0a3.2 3.2 0 1 0 6.4 0`)
  .join('')
const SLOTS = [60, 200].map((z) => [
  line([[120 + 0.18 * z, 700 - 0.5 * z], [1400 + 0.18 * z, 700 - 0.5 * z]]),
  line([[120 + 0.18 * z, 701.5 - 0.5 * z], [1400 + 0.18 * z, 701.5 - 0.5 * z]]),
])

export function Floor() {
  return (
    <g>
      <rect className="f-floor" x="-1100" y="1020" width="3800" height="140" />
      <rect className="f-poly" x="156" y="1020" width="1252" height="6" />
    </g>
  )
}

export function Cabinet() {
  return (
    <g>
      <rect className="f-gR" x="160" y="756" width="1200" height="264" />
      <path className="f-gD" d="M1360 756L1403 636V900L1360 1020Z" />
      <rect className="f-poly" x="160" y="756" width="1200" height="6" />
      <rect className="f-gD" x="160" y="1000" width="1200" height="20" />
      <path className="l-poly" d="M760 762V1000M1060 762V1000M460 762V1000" />
      <path className="l-gT" d="M740 800V860M780 800V860M1040 800V860M480 800V860" />
      <path className="l-gS" d="M160 762V1000" />
    </g>
  )
}

export function Table() {
  return (
    <g>
      <path className="f-poly" d={TABLE_BOX.side} />
      <path className="f-gD" d={TABLE_BOX.front} />
      <path className="f-gR" d={TABLE_BOX.top} />
      {SLOTS.map(([slot, lip]) => (
        <g key={slot}>
          <path className="l-slot" d={slot} />
          <path className="l-gS" d={lip} />
        </g>
      ))}
      <path className="l-gT" d={`${TABLE_BOX.litTop}${TABLE_BOX.litLeft}`} />
      <path className="l-gS" d="M120 704H1400M120 742H1400" />
      <path className="f-gS pp-hair" d={TABLE_SCREWS} />
    </g>
  )
}

/* ---- C-frame (cast Γ): column at the left, beam across the top --------- */

const C_FRONT = 'M262 150H1352V232H406Q382 232 382 256V618H262Z'
const C_FLANGE = box({ x0: 224, x1: 370, z0: 126, z1: 250, h0: 0, h1: 12 })
const FLANGE_BOLTS = [238, 356]
  .map((x) => [x + 0.18 * 134, 700 - 12 - 0.5 * 134])
  .map(([x, y]) => `M${(x + 4).toFixed(1)} ${y.toFixed(1)}a4 2 0 1 0 -8 0a4 2 0 1 0 8 0`)
  .join('')

export function CFrame() {
  return (
    <g>
      <Box geo={C_FLANGE} mat="g" shadow="f-gR" />
      <path className="f-gR pp-hair" d={FLANGE_BOLTS} />
      <path className="f-gS" d="M382 160L400 110V568L382 618Z" />
      <path className="f-gF" d={C_FRONT} />
      <path className="f-gT" d="M262 150H1352L1370 100H280Z" />
      <path className="f-gS" d="M1352 150L1370 100V182L1352 232Z" />
      <path className="l-hi" d="M262 618V150H1352" />
      <path className="l-gR" d="M382 256V618M406 232H1352" />
      <path className="l-etch" d="M332 398a10 10 0 1 0 -20 0a10 10 0 1 0 20 0M322 382V414M306 398H338" />
      <path className="pp-hair l-gS" d="M276 176H368M276 596H368" />
    </g>
  )
}

/* ---- Air service: regulator with gauge on the beam, hose out of frame -- */

export function AirService() {
  return (
    <g>
      <path className="l-tube" d="M321 26V-260" />
      <path className="l-tubeHi" d="M318.8 26V-260" />
      <path className="l-tube" d="M356 76C376 76 378 98 366 112C354 126 316 126 314 146" />
      <path className="l-tubeHi" d="M356 73.8C374 74 375 96 364 110" />
      <rect className="f-aF" x="286" y="86" width="70" height="14" />
      <path className="f-aS" d="M352 30L357 22V78L352 86Z" />
      <rect className="f-aF" x="290" y="30" width="62" height="56" />
      <path className="f-aT" d="M290 30H352L357 22H295Z" />
      <path className="l-spec" d="M290 86V30H352" />
      <circle className="f-cer" cx="321" cy="56" r="16" />
      <circle className="l-gauge" cx="321" cy="56" r="11.5" />
      <path className="l-needle" d="M321 56L329.5 47.5" />
      <circle className="f-poly" cx="321" cy="56" r="2" />
      <rect className="f-aT" x="314" y="18" width="14" height="6" />
    </g>
  )
}

/* ---- Drag chain on the beam top -----------------------------------------
   Fixed end at x 400; the moving end rides the carriage (Xs + 38). The bend
   sits where both runs meet, (400 + Xs + 38 + 800) / 2, so it travels at half
   the carriage speed. Runs are clipped with inset() (scene.css). */

export function DragChain() {
  return (
    <g>
      <rect className="f-aF" x="390" y="114" width="14" height="18" />
      <g className="pp-chain-lower">
        <rect className="f-gR" x="400" y="119" width="1120" height="10" />
        <path className="l-chainPlate" d="M400 124H1520" />
        <path className="l-chainPin" d="M404 124H1520" />
      </g>
      <g className="pp-chain-upper">
        <rect className="f-gR" x="400" y="105" width="1120" height="10" />
        <g className="pp-x">
          <path className="l-chainPlate" d="M38 110H390" />
          <path className="l-chainPin" d="M42 110H390" />
        </g>
      </g>
      <g className="pp-chain-bend">
        <path className="l-chainBend" d="M0 110A7 7 0 0 1 0 124" />
      </g>
    </g>
  )
}

/* ---- X axis: rodless cylinder, linear rail, end caps, absorbers -------- */

const SCREWS = (cx, cy) =>
  [-8, 8].flatMap((dx) => [-12, 12].map((dy) => `M${cx + dx + 3} ${cy + dy}a3 3 0 1 0 -6 0a3 3 0 1 0 6 0`)).join('')

export function XAxis() {
  return (
    <g>
      <XAxisBody />
      <Absorber side="l" />
      <Absorber side="r" />
    </g>
  )
}

function XAxisBody() {
  return (
    <g>
      <rect className="f-aT" x="300" y="154" width="1030" height="4" />
      <rect className="f-aF" x="300" y="158" width="1030" height="28" />
      <path className="l-aS" d="M300 166H1330M300 178H1330" />
      <path className="l-spec" d="M300 158H1330" />
      {[300, 1302].map((x) => (
        <g key={x}>
          <rect className="f-aS" x={x} y="152" width="28" height="40" />
          <path className="l-spec" d={`M${x} 192V152H${x + 28}`} />
          <path className="f-gR pp-hair" d={SCREWS(x + 14, 172)} />
          <rect className="f-aT" x={x + 9} y="146" width="10" height="6" />
        </g>
      ))}
      <path className="l-tube" d="M1316 146C1316 124 1334 112 1366 106" />
      <rect className="f-cA" x="330" y="204" width="970" height="2" />
      <rect className="f-cB" x="330" y="206" width="970" height="4.5" />
      <rect className="f-cC" x="330" y="210.5" width="970" height="3.5" />
      <path className="l-bolts pp-hair" d="M346 209.2H1300" />
    </g>
  )
}

function Absorber({ side }) {
  const left = side === 'l'
  const body = left ? 380 : 1200
  const rod = left ? 'M408 241.5H414V246.5H408Z' : 'M1194 241.5H1200V246.5H1194Z'
  const cap = left ? 'M414 240H418V248H414Z' : 'M1190 240H1194V248H1190Z'
  const bracket = left ? 'M372 232H392V238H386V256H372Z' : 'M1216 232H1236V256H1222V238H1216Z'
  return (
    <g>
      <path className="f-gS" d={bracket} />
      <g className={`pp-abs-${side}`}>
        <path className="f-cB" d={rod} />
        <path className="f-floor" d={cap} />
      </g>
      <rect className="f-aF" x={body} y="238" width="28" height="12" />
      <rect className="f-aT" x={body} y="236" width="28" height="2" />
      <rect className="f-aS" x={left ? body + 22 : body} y="237" width="6" height="14" />
    </g>
  )
}

/* ---- Carriage (translateX(Xs), local x 0 = carriage centre) ------------ */

const PLATE_SCREWS = [-52, 52]
  .flatMap((x) => [162, 250].map((y) => `M${x + 3} ${y}a3 3 0 1 0 -6 0a3 3 0 1 0 6 0`))
  .join('')
const NUTS = [-34, 34].flatMap((x) => [196, 294].map((y) => `M${x - 4} ${y}h8v6h-8Z`)).join('')

export function Carriage() {
  return (
    <g className="pp-x">
      <CarriageBody />
    </g>
  )
}

function CarriageBody() {
  return (
    <g>
      <rect className="f-aF" x="30" y="104" width="16" height="44" />
      <path className="f-aT" d="M30 104H46L48 100H32Z" />
      <path className="f-gS" d="M66 150L67.4 146V258L66 262Z" />
      <rect className="f-gF" x="-66" y="150" width="132" height="112" />
      <path className="f-gT" d="M-66 150H66L67.4 146H-64.6Z" />
      <path className="l-hi" d="M-66 262V150H66" />
      <path className="f-gR" d={PLATE_SCREWS} />
      <path className="l-screw pp-hair" d={PLATE_SCREWS} />
      <path className="f-aS" d="M40 196L47.2 176V280L40 300Z" />
      <rect className="f-aF" x="-40" y="196" width="80" height="104" />
      <path className="f-aT" d="M-40 196H40L47.2 176H-32.8Z" />
      <path className="l-aS pp-hair" d="M-34 202V292M34 202V292" />
      <path className="f-aS" d={NUTS} />
      <path className="l-spec" d="M-40 300V196H40" />
      <path className="f-aS" d="M-34 300h12v4h-12ZM22 300h12v4h-12ZM-7 300h14v4h-14Z" />
      <rect className="f-aT" x="47" y="210" width="7" height="9" />
      <rect className="f-aT" x="47" y="277" width="7" height="9" />
      <path className="l-tube" d="M54 214.5C74 214.5 72 172 46 140" />
      <path className="l-tube" d="M54 281.5C86 281.5 84 168 46 128" />
      <path className="l-tubeHi" d="M54 212.3C71 212 70 176 48 146" />
    </g>
  )
}

/* ---- Annotation pieces: the register's crop marks ---------------------- */

const CROPS = [
  ['M-44 -32V-44H-32', -1, -1],
  ['M32 -44H44V-32', 1, -1],
  ['M44 32V44H32', 1, 1],
  ['M-32 44H-44V32', -1, 1],
]

/**
 * Four L crops lying in a face plane (pass the face matrix), flying in along
 * their diagonals. `scale` sizes the register box (the CSS fly-in transform
 * sits on the paths, so the scale lives on a wrapper).
 */
export function CropMarks({ transform, scale = 1 }) {
  return (
    <g transform={transform}>
      <g transform={scale === 1 ? undefined : `scale(${scale})`}>
        {CROPS.map(([d, sx, sy], i) => (
          <path key={d} className={`pp-crop pp-crop-${i}`} d={d} style={{ '--dx': sx * 26, '--dy': sy * 26 }} />
        ))}
      </g>
    </g>
  )
}
